import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const images = [
  { src: "/images/img16.jpeg", alt: "Ekamdhyas image" },
  { src: "/images/img9.jpeg", alt: "Ekamdhyas image" },
  { src: "/images/img0.jpeg", alt: "Ekamdhyas image" },
];

export default function WhoWeAre() {
  const [index, setIndex] = useState(0);
  const [direction, setDirection] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setDirection(1);
      setIndex((prev) => (prev + 1) % images.length);
    }, 50000);
    return () => clearInterval(interval);
  }, []);

  const handlePrevious = () => {
    setDirection(-1);
    setIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const handleNext = () => {
    setDirection(1);
    setIndex((prev) => (prev + 1) % images.length);
  };

  const slideVariants = {
    enter: {
      opacity: 0,
    },
    center: {
      opacity: 1,
    },
    exit: {
      opacity: 0,
    },
  };

  return (
    <section className="relative bg-gradient-to-br from-slate-50 via-indigo-50/30 to-purple-50/20 overflow-hidden py-16 sm:py-20 lg:py-28">
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-32 -left-32 w-96 h-96 bg-gradient-to-br from-indigo-200/40 to-purple-200/40 rounded-full blur-3xl" />
        <div className="absolute -bottom-32 -right-32 w-96 h-96 bg-gradient-to-tl from-purple-200/40 to-indigo-200/40 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div className="relative order-2 lg:order-1">
            <div className="relative group">
              <div className="absolute -inset-4 bg-gradient-to-r from-indigo-500/20 via-purple-500/20 to-pink-500/20 rounded-3xl blur-2xl group-hover:blur-3xl transition-all duration-500" />
              <div className="absolute -inset-2 bg-gradient-to-br from-white/40 to-white/10 rounded-2xl backdrop-blur-sm" />

              <div className="relative aspect-[15/9] rounded-2xl overflow-hidden bg-slate-900 shadow-2xl">
                <AnimatePresence
                  initial={false}
                  custom={direction}
                  mode="popLayout"
                >
                  <motion.div
                    key={index}
                    custom={direction}
                    variants={slideVariants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{
                      duration: 1,
                      ease: "easeInOut",
                    }}
                    className="absolute inset-0"
                  >
                    <img
                      src={images[index].src}
                      alt={images[index].alt}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/40 via-transparent to-transparent" />
                  </motion.div>
                </AnimatePresence>

                <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 flex items-center justify-between px-4 z-10">
                  <button
                    onClick={handlePrevious}
                    className="group/btn w-12 h-12 rounded-full bg-white/90 backdrop-blur-md shadow-lg 
                               hover:bg-white transition-all duration-300 flex items-center justify-center
                               border border-white/20 hover:shadow-xl"
                    aria-label="Previous image"
                  >
                    <ChevronLeft className="w-6 h-6 text-slate-700 group-hover/btn:text-indigo-600 transition-colors" />
                  </button>

                  <button
                    onClick={handleNext}
                    className="group/btn w-12 h-12 rounded-full bg-white/90 backdrop-blur-md shadow-lg 
                               hover:bg-white transition-all duration-300 flex items-center justify-center
                               border border-white/20 hover:shadow-xl"
                    aria-label="Next image"
                  >
                    <ChevronRight className="w-6 h-6 text-slate-700 group-hover/btn:text-indigo-600 transition-colors" />
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-center gap-2 mt-6">
                {images.map((_, idx) => (
                  <button
                    key={idx}
                    onClick={() => {
                      setDirection(idx > index ? 1 : -1);
                      setIndex(idx);
                    }}
                    className="relative group/dot"
                    aria-label={`View image ${idx + 1}`}
                  >
                    <div
                      className={`rounded-full transition-all duration-300 ${
                        index === idx
                          ? "w-10 h-2.5 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600"
                          : "w-2.5 h-2.5 bg-slate-300 group-hover/dot:bg-slate-400"
                      }`}
                    />
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-8 order-1 lg:order-2">
            <div>
              <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl
                           font-bold text-slate-900 leading-tight tracking-tight">
                Who We Are
              </h2>

              <div className="mt-6 flex items-center gap-3">
                <span className="h-1.5 rounded-full bg-gradient-to-r from-blue-600 to-cyan-500 w-24" />
                <div className="flex gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-indigo-400" />
                  <span className="w-2 h-2 rounded-full bg-purple-400" />
                  <span className="w-2 h-2 rounded-full bg-pink-400" />
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <p className="text-base sm:text-lg text-slate-600 leading-relaxed">
                <strong className="text-slate-900 font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
                  Ekamdhyas
                </strong>{" "}
                is an education-focused organization dedicated to creating
                structured and meaningful learning experiences.
              </p>

              <p className="text-base sm:text-lg text-slate-600 leading-relaxed">
                We believe clarity, discipline, and thoughtful guidance form the
                foundation of long-term growth. Our work is centered on helping
                learners understand their direction and progress with
                confidence.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
